import React, { useState, useCallback, useEffect } from "react";
import Loader from "react-loader";
import { useLocation, withRouter } from "react-router-dom";
import { ErrorModal, InfoModal } from "./AppModal";
import { useGroups } from "./Sync";
import ScheduleForm from "./components/pages/ScheduleForm";
import {
  APP_ENDPOINT,
  SCHEDULE_TIME_FLOW_NAME,
  SCHEDULE_APP_INITIAL_DATA_ID,
  SI_API_ENDPOINT,
  API_ENDPOINT,
  APPLICATION_NAME,
} from "./utils/constants";
import {
  AppInstance,
  AppSetting,
  Profile,
  ScheduleSetting,
  AutomaticalSyncSetting,
  Group,
} from "./utils/types";
import { put, get, post } from "./utils/fetch";
import AddAppInstanceForm from "./components/pages/AddAppInstanceForm";
import { getAuthorizationUrl } from "./Verify";

export const GROUP_DEFAULT_VALUE = "All";

function Schedule() {
  const {
    state: { accessToken },
  } = useLocation();

  const { groups, loading: groupsLoading, error: groupsError } = useGroups(
    accessToken,
  );
  const {
    loading: appInstanceLoading,
    info: appInstanceInfo,
    initInfo: initAppInstanceInfo,
    appInstance,
    doFetchAppInstance,
  } = useAppInstance();
  const {
    info: scheduleInfo,
    initInfo: initScheduleInfo,
    loading: schduleLoading,
    error: scheduleError,
    autoSyncSetting,
    doUpsertAutoSyncSetting,
  } = useAutoSyncSetting(
    groups,
    appInstance ? appInstance.positionId : undefined,
  );

  const loading = groupsLoading || appInstanceLoading || schduleLoading;
  const info = appInstanceInfo || scheduleInfo;
  const error = groupsError || scheduleError;
  const saveButtonDisable = loading || info !== "" || error !== "";
  const initInfo = () => {
    initAppInstanceInfo();
    initScheduleInfo();
  };

  return (
    <>
      {!loading && !appInstance && (
        <AddAppInstanceForm onSubmit={doFetchAppInstance} />
      )}
      {appInstance && autoSyncSetting && (
        <ScheduleForm
          defaultValue={autoSyncSetting}
          groups={groups}
          saveButtonDisable={saveButtonDisable}
          onSubmit={async setting => {
            await doUpsertAutoSyncSetting(accessToken, setting);
          }}
        />
      )}
      <Loader loaded={!loading} className="react-loader" />
      <InfoModal show={info !== ""} body={info} callback={initInfo} />
      <ErrorModal show={error !== ""} body={error} callback={() => {}} />
    </>
  );
}

const useAppInstance = () => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [appInstance, setAppInstance] = useState<AppInstance>();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const profile = await fetchProfile();
        const targetAppInstance = profile.appInstances.find(
          appInstance =>
            appInstance.appInitialDataId === SCHEDULE_APP_INITIAL_DATA_ID,
        );
        // 初めて自動設定をするテナントにはプロファイルにGoogleDirectoryのアプリが追加されてないので
        // 存在しなくても初回レンダリング時はエラーにしない。
        setAppInstance(targetAppInstance);
      } catch (e) {
        setInfo(JSON.stringify(e.message));
      }
      setLoading(false);
    })();
  }, []);

  const doFetchAppInstance = useCallback(async () => {
    setLoading(true);
    try {
      const profile = await fetchProfile();
      const targetAppInstance = profile.appInstances.find(
        appInstance =>
          appInstance.appInitialDataId === SCHEDULE_APP_INITIAL_DATA_ID,
      );
      if (!targetAppInstance) {
        setInfo("Please add an application with the above step");
      }
      setAppInstance(targetAppInstance);
    } catch (e) {
      setInfo(JSON.stringify(e.message));
    }
    setLoading(false);
  }, []);

  const initInfo = () => {
    setInfo("");
  };

  return {
    loading,
    info,
    initInfo,
    appInstance,
    doFetchAppInstance,
  };
};

const useAutoSyncSetting = (groups: Group[], positionId?: number) => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [schedule, setSchedule] = useState<ScheduleSetting>();
  const [appSetting, setAppSetting] = useState<AppSetting>();

  useEffect(() => {
    if (positionId) {
      fetchSetting(positionId);
    }
  }, [positionId]);

  const fetchSetting = async (positionId: number) => {
    setLoading(true);
    try {
      const [appSetting, schedule] = await Promise.all([
        fetchAppSetting(positionId),
        fetchSchedule(positionId),
      ]);
      setAppSetting(appSetting);
      setSchedule(schedule);
    } catch (e) {
      setError(JSON.stringify((e as any).message));
    }
    setLoading(false);
  };

  const doUpsertAutoSyncSetting = useCallback(
    async (accessToken: string, setting: AutomaticalSyncSetting) => {
      if (!positionId || !appSetting) {
        return;
      }
      const { scheduleEnabled, group, query, enableDelete } = setting;

      const { app_section_id, ...other } = appSetting;

      const newAppSetting: AppSetting = {
        ...other,
        app_section_id: {
          ...app_section_id,
          postCustomParameter_groupKey_text:
            group === GROUP_DEFAULT_VALUE ? null : group,
          postCustomParameter_query_text: query,
          postCustomParameter_isDelete_bool: enableDelete,
        },
      };

      try {
        setLoading(true);
        let time;
        if (
          schedule &&
          schedule.timeSchedules &&
          schedule.timeSchedules[0].byTime.length !== 0
        ) {
          // GoogleWorkspace同期アプリではスケジュール実行の時間指定は最大1つまで。GoogleWorkspace同期アプリを利用するテナントが増えても
          // 認証基盤への負荷を均等に分散したい。発行されるスケジュール実行時刻は一定間隔に発行されるので一度設定したスケジュール実行時刻の
          // 更新はしない。
          time = schedule.timeSchedules[0].byTime[0];
        } else {
          time = await issueScheduleTime(accessToken);
        }
        const [updatedAppSetting, updatedSchedule] = await Promise.all([
          upsertAppSetting(positionId, newAppSetting),
          upsertScheduleSetting(positionId, scheduleEnabled, time),
        ]);
        setAppSetting(updatedAppSetting);
        setSchedule(updatedSchedule);
        setInfo("Changes have been saved.");
      } catch (e) {
        setError("useScheduleSetting: " + JSON.stringify((e as any).message));
      }
      setLoading(false);
    },
    [appSetting, schedule, positionId],
  );

  const initInfo = () => {
    setInfo("");
  };

  const autoSyncSetting = appSetting &&
    schedule && {
      scheduleEnabled: schedule.enabled,
      query: appSetting.app_section_id.postCustomParameter_query_text
        ? appSetting.app_section_id.postCustomParameter_query_text
        : "",
      group:
        appSetting.app_section_id.postCustomParameter_groupKey_text &&
        groups.some(
          group =>
            appSetting.app_section_id.postCustomParameter_groupKey_text ===
            group.email,
        )
          ? appSetting.app_section_id.postCustomParameter_groupKey_text
          : GROUP_DEFAULT_VALUE, // アプリ設定に登録済みのグループが削除された場合はデフォルト値にする
      enableDelete: appSetting.app_section_id.postCustomParameter_isDelete_bool
        ? appSetting.app_section_id.postCustomParameter_isDelete_bool
        : "false",
    };

  return {
    info,
    initInfo,
    loading,
    error,
    doUpsertAutoSyncSetting,
    autoSyncSetting,
  };
};

const fetchProfile = async (): Promise<Profile> => {
  const responseJson = await get(
    `${API_ENDPOINT}/appdata/tenant/admin/profiles/default`,
    {},
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(
        `${APP_ENDPOINT}/schedule`,
      );
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (responseJson.error) {
    throw Error(responseJson.error);
  }
  return responseJson;
};

const fetchAppSetting = async (positionId: number): Promise<AppSetting> => {
  const responseJson = await get(
    `${API_ENDPOINT}/appdata/tenant/admin/position/appinstances/${positionId}/setting`,
    {},
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(
        `${APP_ENDPOINT}/schedule`,
      );
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (responseJson.error) {
    throw Error(responseJson.error);
  }

  return responseJson;
};

const upsertAppSetting = async (
  positionId: number,
  appSetting: AppSetting,
): Promise<AppSetting> => {
  const responseJson = await put(
    `${API_ENDPOINT}/appdata/tenant/admin/position/appinstances/${positionId}/setting`,
    {
      body: JSON.stringify(appSetting),
    },
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(
        `${APP_ENDPOINT}/schedule`,
      );
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (responseJson.error) {
    throw Error(responseJson.error);
  }
  return responseJson;
};

const fetchSchedule = async (positionId: number): Promise<ScheduleSetting> => {
  const responseJson = await get(
    `${API_ENDPOINT}/appdata/tenant/admin/position/appinstances/${positionId}/settings/schedule`,
    {},
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(
        `${APP_ENDPOINT}/schedule`,
      );
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (responseJson.error) {
    throw Error(responseJson.error);
  }

  return responseJson;
};

const upsertScheduleSetting = async (
  positionId: number,
  enabled: boolean,
  time: string,
): Promise<ScheduleSetting> => {
  const responseJson = await put(
    `${API_ENDPOINT}/appdata/tenant/admin/position/appinstances/${positionId}/settings/schedule`,
    {
      body: JSON.stringify({
        enabled,
        timeSchedules: [
          {
            byTime: [time],
          },
        ],
        dayOfWeeks: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
      }),
    },
    "include",
  );
  if (
    responseJson.errors &&
    responseJson.errors[0].message_id === "error.wrong_ticket"
  ) {
    try {
      window.location.href = await getAuthorizationUrl(
        `${APP_ENDPOINT}/schedule`,
      );
    } catch (e) {
      throw Error("doAuthorization:" + JSON.stringify(e.message));
    }
  }
  if (responseJson.error) {
    throw Error(responseJson.error);
  }
  return responseJson;
};

const issueScheduleTime = async (accessToken: string): Promise<string> => {
  /**
   * そのコンポーネントを実行するフローは手動同期のWFアプリが所持している。理由は、自動同期のWFアプリにそのフローを所持させた
   * 場合、アクセストークンを取得するためにそのClientIDの情報も必要になりGoogleWorkspace同期アプリで管理する情報が増えるから。
   */
  const responseJson = await post(
    `${SI_API_ENDPOINT}/v1/io/applications/${APPLICATION_NAME}/flows/${SCHEDULE_TIME_FLOW_NAME}/jobs`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        parameters: {},
        sync: true,
        debugMode: true,
      }),
    },
  );
  if (!responseJson.time) {
    throw Error(responseJson.error);
  }
  return responseJson.time;
};

export default withRouter(Schedule);
